import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { EmptyState, StatusBadge } from "@/components/app/ui-bits";
import { Calculator, Receipt, Users, Bank, ListChecks } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { TaxTab } from "@/pages/finance/PhaseC";
import { BANK_ACCOUNTS } from "./_shared";

const fmt = (v) => (v == null ? "—" : formatCurrency(v));
const PILLS = [
  { v: "summary", l: "Summary" },
  { v: "vat", l: "VAT / Sales Tax" },
  { v: "paye", l: "PAYE (Payroll)" },
  { v: "cit", l: "Corporate Income Tax" },
  { v: "tracker", l: "Liability Tracker" },
];

export default function TaxCenterTab({ canEdit }) {
  const [pill, setPill] = useState("summary");
  return (
    <div className="space-y-4" data-testid="tax-center-tab">
      <div className="flex flex-wrap gap-1.5">
        {PILLS.map((p) => (
          <button key={p.v} onClick={() => setPill(p.v)} data-testid={`tax-pill-${p.v}`}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${pill === p.v ? "bg-[#1E5CBA] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {p.l}
          </button>
        ))}
      </div>
      {pill === "summary" && <TaxSummary />}
      {pill === "vat" && <VatSection canEdit={canEdit} />}
      {pill === "paye" && <PayeSection canEdit={canEdit} />}
      {pill === "cit" && <CitSection canEdit={canEdit} />}
      {pill === "tracker" && <TaxTab canEdit={canEdit} />}
    </div>
  );
}

// ─────────────────────────── Summary ───────────────────────────
function TaxSummary() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/finance/tax/summary").then(({ data: d }) => setData(d)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  const cit = data.cit;
  const nextCit = cit?.installments?.find((i) => i.status !== "paid");
  return (
    <div className="space-y-4" data-testid="tax-summary">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="dc-card p-4 border-l-4 border-l-[#1E5CBA]">
          <div className="overline">VAT net position · {data.vat.period}</div>
          <div className="mt-1 font-display text-2xl font-bold tabular-nums" data-testid="tax-kpi-vat">{fmt(data.vat.net_position)}</div>
          <div className="text-xs text-slate-500">Output {fmt(data.vat.output_tax)} − Input {fmt(data.vat.input_tax)}</div>
        </div>
        <div className="dc-card p-4 border-l-4 border-l-amber-500">
          <div className="overline">PAYE outstanding</div>
          <div className="mt-1 font-display text-2xl font-bold tabular-nums" data-testid="tax-kpi-paye">{fmt(data.paye.outstanding)}</div>
          <div className="text-xs text-slate-500">unremitted returns (draft/filed)</div>
        </div>
        <div className="dc-card p-4 border-l-4 border-l-emerald-500">
          <div className="overline">CIT next installment</div>
          <div className="mt-1 font-display text-2xl font-bold tabular-nums" data-testid="tax-kpi-cit">{nextCit ? fmt(nextCit.amount) : "—"}</div>
          <div className="text-xs text-slate-500">{nextCit ? `Q${nextCit.no} due ${formatDate(nextCit.due_date)}` : cit ? "all installments paid" : "no projection yet"}</div>
        </div>
        <div className="dc-card p-4 border-l-4 border-l-rose-500">
          <div className="overline">Penalty exposure</div>
          <div className="mt-1 font-display text-2xl font-bold tabular-nums text-rose-600" data-testid="tax-kpi-penalty">{fmt(data.penalty_exposure_total)}</div>
          <div className="text-xs text-slate-500">$500 / late month per obligation</div>
        </div>
      </div>
      <div className="dc-card overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-3">
          <h3 className="font-display text-[15px] font-semibold text-slate-900">Upcoming tax deadlines</h3>
        </div>
        {data.deadlines.length === 0 ? (
          <div className="px-5 py-6 text-sm text-slate-500">No open tax obligations. Prepare VAT/PAYE returns or a CIT projection to see deadlines here.</div>
        ) : (
          <table className="data-table w-full">
            <thead><tr><th>Type</th><th>Obligation</th><th>Due</th><th className="text-right">Amount</th><th>Status</th><th className="text-right">Penalty if unpaid</th></tr></thead>
            <tbody>
              {data.deadlines.map((d, i) => (
                <tr key={i} className="text-sm" data-testid={`tax-deadline-${d.kind}-${i}`}>
                  <td className="px-5 py-2.5"><span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono-alt text-[10px] font-bold">{d.kind}</span></td>
                  <td className="px-5 py-2.5">{d.label}</td>
                  <td className={`px-5 py-2.5 font-mono-alt text-xs tabular-nums ${d.months_late > 0 ? "font-bold text-rose-600" : ""}`}>{formatDate(d.due_date)}{d.months_late > 0 && ` · ${d.months_late} mo late`}</td>
                  <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums">{fmt(d.amount)}</td>
                  <td className="px-5 py-2.5"><StatusBadge status={d.status} /></td>
                  <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums text-rose-600">{d.penalty_exposure ? fmt(d.penalty_exposure) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────── Shared pay dialog ───────────────────────────
function TaxPayDialog({ open, onClose, title, amount, dueDate, onPay }) {
  const [form, setForm] = useState({ payment_date: new Date().toISOString().slice(0, 10), bank_account: BANK_ACCOUNTS[0], include_penalty: false, reference: "" });
  const monthsLate = (() => {
    if (!dueDate) return 0;
    const a = new Date(form.payment_date), d = new Date(dueDate);
    if (a <= d) return 0;
    let n = (a.getFullYear() - d.getFullYear()) * 12 + (a.getMonth() - d.getMonth());
    if (a.getDate() > d.getDate()) n += 1;
    return Math.max(1, n);
  })();
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Amount due {fmt(amount)}{dueDate && ` · deadline ${formatDate(dueDate)}`}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Payment date</Label><Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} data-testid="tax-pay-date" /></div>
          <div>
            <Label>Bank</Label>
            <Select value={form.bank_account} onValueChange={(v) => setForm({ ...form, bank_account: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{BANK_ACCOUNTS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
          {monthsLate > 0 && (
            <div className="col-span-2 flex items-center gap-2 rounded-md bg-rose-50 border border-rose-200 p-2.5">
              <Checkbox checked={form.include_penalty} onCheckedChange={(v) => setForm({ ...form, include_penalty: !!v })} data-testid="tax-pay-penalty-check" />
              <span className="text-xs text-rose-700">Include late penalty: {monthsLate} month(s) × $500 = <b>{fmt(monthsLate * 500)}</b></span>
            </div>
          )}
        </div>
        <DialogFooter><Button className="btn-primary-blue" onClick={() => onPay(form)} data-testid="tax-pay-confirm">Pay & post to GL</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReturnActions({ row, canEdit, onFile, onPayOpen }) {
  if (!canEdit) return null;
  return (
    <div className="flex gap-1">
      {row.status === "draft" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onFile} data-testid={`tax-file-${row.period}`}>File</Button>}
      {row.status === "filed" && <Button size="sm" className="btn-primary-blue h-7 text-xs" onClick={onPayOpen} data-testid={`tax-pay-${row.period}`}>Pay</Button>}
    </div>
  );
}

// ─────────────────────────── VAT ───────────────────────────
function VatSection({ canEdit }) {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [register, setRegister] = useState(null);
  const [returns, setReturns] = useState([]);
  const [payRow, setPayRow] = useState(null);

  const load = async () => {
    try {
      const [reg, ret] = await Promise.all([
        api.get("/finance/tax/vat/register", { params: { period } }),
        api.get("/finance/tax/vat/returns"),
      ]);
      setRegister(reg.data);
      setReturns(ret.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const prepare = async () => {
    try {
      await api.post("/finance/tax/vat/returns/prepare", { period });
      toast.success(`VAT return prepared for ${period}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const fileReturn = async (r) => {
    try { await api.post(`/finance/tax/vat/returns/${r.id}/file`); toast.success("Return filed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const pay = async (form) => {
    try {
      await api.post(`/finance/tax/vat/returns/${payRow.id}/pay`, form);
      toast.success("VAT paid — journal posted");
      setPayRow(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="vat-section">
      <div className="dc-card p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Period</Label>
          <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="h-9 w-44" data-testid="vat-period-input" />
        </div>
        {register && (
          <div className="flex gap-5 text-sm">
            <div><span className="overline block">Output tax</span><b className="font-mono-alt tabular-nums" data-testid="vat-output-tax">{fmt(register.output_tax)}</b></div>
            <div><span className="overline block">Input tax</span><b className="font-mono-alt tabular-nums" data-testid="vat-input-tax">{fmt(register.input_tax)}</b></div>
            <div><span className="overline block">Net due</span><b className={`font-mono-alt tabular-nums ${register.net_due > 0 ? "text-rose-600" : "text-emerald-700"}`} data-testid="vat-net-due">{fmt(register.net_due)}</b></div>
          </div>
        )}
        {canEdit && <Button size="sm" className="btn-primary-blue h-9 ml-auto" onClick={prepare} data-testid="vat-prepare-btn"><Receipt size={13} className="mr-1" /> Prepare return</Button>}
      </div>

      {register && (register.outputs.length > 0 || register.inputs.length > 0) && (
        <div className="dc-card overflow-hidden">
          <div className="border-b border-slate-100 px-5 py-3"><h3 className="font-display text-[15px] font-semibold">Tax register · {period}</h3></div>
          <table className="data-table w-full">
            <thead><tr><th>Direction</th><th>Doc</th><th>Party</th><th>Date</th><th>Code</th><th className="text-right">Subtotal</th><th className="text-right">Tax</th></tr></thead>
            <tbody>
              {register.outputs.map((x, i) => (
                <tr key={`o${i}`} className="text-sm"><td className="px-5 py-2"><span className="rounded bg-emerald-50 px-1.5 text-[10px] font-bold text-emerald-700">OUTPUT</span></td><td className="px-5 py-2 font-mono-alt text-xs">{x.doc_no}</td><td className="px-5 py-2">{x.party}</td><td className="px-5 py-2 font-mono-alt text-xs">{formatDate(x.date)}</td><td className="px-5 py-2 font-mono-alt text-xs">{x.tax_code}</td><td className="px-5 py-2 text-right font-mono-alt tabular-nums">{fmt(x.subtotal)}</td><td className="px-5 py-2 text-right font-mono-alt tabular-nums font-semibold">{fmt(x.tax)}</td></tr>
              ))}
              {register.inputs.map((x, i) => (
                <tr key={`i${i}`} className="text-sm"><td className="px-5 py-2"><span className="rounded bg-blue-50 px-1.5 text-[10px] font-bold text-blue-700">INPUT</span></td><td className="px-5 py-2 font-mono-alt text-xs">{x.doc_no}</td><td className="px-5 py-2">{x.party}</td><td className="px-5 py-2 font-mono-alt text-xs">{formatDate(x.date)}</td><td className="px-5 py-2 font-mono-alt text-xs">{x.tax_code}</td><td className="px-5 py-2 text-right font-mono-alt tabular-nums">{fmt(x.subtotal)}</td><td className="px-5 py-2 text-right font-mono-alt tabular-nums font-semibold">{fmt(x.tax)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="dc-card overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-3"><h3 className="font-display text-[15px] font-semibold">VAT returns</h3></div>
        {returns.length === 0 ? (
          <div className="px-5 py-6 text-sm text-slate-500">No returns prepared yet.</div>
        ) : (
          <table className="data-table w-full">
            <thead><tr><th>Period</th><th className="text-right">Output</th><th className="text-right">Input</th><th className="text-right">Net due</th><th>Due date</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {returns.map((r) => (
                <tr key={r.id} className="text-sm" data-testid={`vat-return-${r.period}`}>
                  <td className="px-5 py-2.5 font-mono-alt">{r.period}</td>
                  <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums">{fmt(r.output_tax)}</td>
                  <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums">{fmt(r.input_tax)}</td>
                  <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums font-semibold">{fmt(r.net_due)}</td>
                  <td className="px-5 py-2.5 font-mono-alt text-xs">{formatDate(r.due_date)}</td>
                  <td className="px-5 py-2.5"><StatusBadge status={r.status} /></td>
                  <td className="px-5 py-2.5"><ReturnActions row={r} canEdit={canEdit} onFile={() => fileReturn(r)} onPayOpen={() => setPayRow(r)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <TaxPayDialog open={!!payRow} onClose={() => setPayRow(null)} title={`Pay VAT · ${payRow?.period}`} amount={payRow?.net_due} dueDate={payRow?.due_date} onPay={pay} />
    </div>
  );
}

// ─────────────────────────── PAYE ───────────────────────────
function PayeSection({ canEdit }) {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [returns, setReturns] = useState([]);
  const [payRow, setPayRow] = useState(null);
  const [calcGross, setCalcGross] = useState("");

  const payeOf = (g) => {
    const x = parseFloat(g || 0);
    if (x <= 100) return 0;
    if (x <= 500) return (x - 100) * 0.06;
    if (x <= 1500) return 24 + (x - 500) * 0.12;
    return 144 + (x - 1500) * 0.18;
  };

  const load = async () => {
    try {
      const { data } = await api.get("/finance/tax/paye/returns");
      setReturns(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const prepare = async () => {
    try {
      const { data } = await api.post("/finance/tax/paye/returns/prepare", { period });
      toast.success(`PAYE return prepared · ${data.employee_count} employee(s) · ${fmt(data.total_paye)}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const fileReturn = async (r) => {
    try { await api.post(`/finance/tax/paye/returns/${r.id}/file`); toast.success("Return filed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const pay = async (form) => {
    try {
      await api.post(`/finance/tax/paye/returns/${payRow.id}/pay`, form);
      toast.success("PAYE remitted — journal posted");
      setPayRow(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="paye-section">
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="dc-card p-4">
          <div className="flex items-center gap-2 mb-2"><Calculator size={16} style={{ color: "#1E5CBA" }} /><h3 className="font-display text-[15px] font-semibold">PAYE calculator (Second Schedule)</h3></div>
          <div className="flex items-end gap-3">
            <div><Label className="text-xs">Monthly gross (USD)</Label><Input type="number" step="0.01" className="h-9 w-40" value={calcGross} onChange={(e) => setCalcGross(e.target.value)} data-testid="paye-calc-gross" /></div>
            <div className="pb-1 text-sm">→ PAYE <b className="font-mono-alt tabular-nums" data-testid="paye-calc-result">{fmt(payeOf(calcGross))}</b> · Net <b className="font-mono-alt tabular-nums">{fmt(parseFloat(calcGross || 0) - payeOf(calcGross))}</b></div>
          </div>
          <div className="mt-3 text-[11px] text-slate-500">
            0–100: exempt · 100–500: 6% above $100 · 500–1500: $24 + 12% above $500 · 1500+: $144 + 18% above $1500. Deducted automatically on every salary batch.
          </div>
        </div>
        <div className="dc-card p-4 flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">Return period</Label><Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="h-9 w-44" data-testid="paye-period-input" /></div>
          {canEdit && <Button size="sm" className="btn-primary-blue h-9" onClick={prepare} data-testid="paye-prepare-btn"><Users size={13} className="mr-1" /> Prepare monthly return</Button>}
          <div className="w-full text-[11px] text-slate-500">Pulls withheld PAYE from salary batches & requests for the month. Due the 15th of the following month · $500/month late penalty.</div>
        </div>
      </div>

      <div className="dc-card overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-3"><h3 className="font-display text-[15px] font-semibold">PAYE returns</h3></div>
        {returns.length === 0 ? (
          <div className="px-5 py-6 text-sm text-slate-500">No returns prepared yet.</div>
        ) : (
          <table className="data-table w-full">
            <thead><tr><th>Period</th><th className="text-right">Employees</th><th className="text-right">Gross</th><th className="text-right">PAYE withheld</th><th>Due date</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {returns.map((r) => (
                <tr key={r.id} className="text-sm" data-testid={`paye-return-${r.period}`}>
                  <td className="px-5 py-2.5 font-mono-alt">{r.period}</td>
                  <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums">{r.employee_count}</td>
                  <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums">{fmt(r.total_gross)}</td>
                  <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums font-semibold">{fmt(r.total_paye)}</td>
                  <td className="px-5 py-2.5 font-mono-alt text-xs">{formatDate(r.due_date)}</td>
                  <td className="px-5 py-2.5"><StatusBadge status={r.status} /></td>
                  <td className="px-5 py-2.5"><ReturnActions row={r} canEdit={canEdit} onFile={() => fileReturn(r)} onPayOpen={() => setPayRow(r)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <TaxPayDialog open={!!payRow} onClose={() => setPayRow(null)} title={`Remit PAYE · ${payRow?.period}`} amount={payRow?.total_paye} dueDate={payRow?.due_date} onPay={pay} />
    </div>
  );
}

// ─────────────────────────── CIT ───────────────────────────
function CitSection({ canEdit }) {
  const [projections, setProjections] = useState([]);
  const [form, setForm] = useState({ fiscal_year: new Date().getFullYear(), projected_revenue: "", projected_expenses: "", use_actuals: false });
  const [payTarget, setPayTarget] = useState(null); // {year, inst}

  const load = async () => {
    try {
      const { data } = await api.get("/finance/tax/cit/projections");
      setProjections(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = form.use_actuals
        ? { fiscal_year: parseInt(form.fiscal_year), use_actuals: true }
        : { fiscal_year: parseInt(form.fiscal_year), projected_revenue: parseFloat(form.projected_revenue || 0), projected_expenses: parseFloat(form.projected_expenses || 0) };
      const { data } = await api.post("/finance/tax/cit/projections", body);
      toast.success(`Projection saved — CIT ${fmt(data.cit_total)} in 4 installments`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const accrue = async (year, no) => {
    try { await api.post(`/finance/tax/cit/${year}/installments/${no}/accrue`); toast.success(`Q${no} accrued to GL`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const pay = async (formData) => {
    try {
      await api.post(`/finance/tax/cit/${payTarget.year}/installments/${payTarget.inst.no}/pay`, formData);
      toast.success(`Q${payTarget.inst.no} installment paid`);
      setPayTarget(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const finalize = async (year) => {
    try {
      const { data } = await api.post(`/finance/tax/cit/${year}/finalize`, {});
      toast.success(`Final return computed — balance due ${fmt(data.finalized.balance_due)}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="cit-section">
      {canEdit && (
        <div className="dc-card p-4">
          <h3 className="font-display text-[15px] font-semibold mb-2">Projected profit statement — CIT 15% in 4 quarterly installments</h3>
          <div className="flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">Fiscal year</Label><Input type="number" className="h-9 w-28" value={form.fiscal_year} onChange={(e) => setForm({ ...form, fiscal_year: e.target.value })} data-testid="cit-year-input" /></div>
            {!form.use_actuals && (
              <>
                <div><Label className="text-xs">Projected revenue</Label><Input type="number" step="0.01" className="h-9 w-40" value={form.projected_revenue} onChange={(e) => setForm({ ...form, projected_revenue: e.target.value })} data-testid="cit-revenue-input" /></div>
                <div><Label className="text-xs">Projected expenses</Label><Input type="number" step="0.01" className="h-9 w-40" value={form.projected_expenses} onChange={(e) => setForm({ ...form, projected_expenses: e.target.value })} data-testid="cit-expenses-input" /></div>
              </>
            )}
            <label className="flex items-center gap-2 pb-2 text-xs text-slate-600">
              <Checkbox checked={form.use_actuals} onCheckedChange={(v) => setForm({ ...form, use_actuals: !!v })} data-testid="cit-use-actuals" />
              Annualize from GL actuals
            </label>
            <Button size="sm" className="btn-primary-blue h-9" onClick={save} data-testid="cit-save-btn">Save projection</Button>
          </div>
          <div className="mt-2 text-[11px] text-slate-500">Installments due Mar 31 · Jun 30 · Sep 30 · Dec 31 (Articles 97–98). Final return due Mar 31 of the following year. $500/month late penalty.</div>
        </div>
      )}

      {projections.length === 0 ? (
        <EmptyState icon={Bank} title="No CIT projection" description="Create a projected profit statement to generate the quarterly installment schedule." />
      ) : projections.map((p) => (
        <div key={p.id} className="dc-card overflow-hidden" data-testid={`cit-projection-${p.fiscal_year}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
            <div>
              <h3 className="font-display text-[15px] font-semibold">FY{p.fiscal_year} — profit {fmt(p.projected_profit)} → CIT (15%) <span className="font-mono-alt" data-testid={`cit-total-${p.fiscal_year}`}>{fmt(p.cit_total)}</span></h3>
              <div className="text-[11px] text-slate-400">rev {fmt(p.projected_revenue)} − exp {fmt(p.projected_expenses)} · {p.method} · final return due {formatDate(p.final_return_due)}</div>
            </div>
            {canEdit && !p.finalized && (
              <Button size="sm" variant="outline" className="h-8" onClick={() => finalize(p.fiscal_year)} data-testid={`cit-finalize-${p.fiscal_year}`}>
                <ListChecks size={13} className="mr-1" /> Compute final return
              </Button>
            )}
          </div>
          <table className="data-table w-full">
            <thead><tr><th>Quarter</th><th>Deadline</th><th className="text-right">Installment</th><th>Status</th><th>Journal</th><th>Actions</th></tr></thead>
            <tbody>
              {p.installments.map((i) => (
                <tr key={i.no} className="text-sm" data-testid={`cit-inst-${p.fiscal_year}-q${i.no}`}>
                  <td className="px-5 py-2.5 font-mono-alt font-semibold">Q{i.no}</td>
                  <td className={`px-5 py-2.5 font-mono-alt text-xs ${i.months_late > 0 ? "font-bold text-rose-600" : ""}`}>{formatDate(i.due_date)}{i.months_late > 0 && ` · ${i.months_late} mo late`}</td>
                  <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums font-semibold">{fmt(i.amount)}</td>
                  <td className="px-5 py-2.5"><StatusBadge status={i.status} /></td>
                  <td className="px-5 py-2.5 font-mono-alt text-xs">{i.entry_no || "—"}</td>
                  <td className="px-5 py-2.5">
                    {canEdit && i.status === "pending" && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => accrue(p.fiscal_year, i.no)} data-testid={`cit-accrue-${p.fiscal_year}-q${i.no}`}>Accrue</Button>
                        <Button size="sm" className="btn-primary-blue h-7 text-xs" onClick={() => setPayTarget({ year: p.fiscal_year, inst: i })} data-testid={`cit-pay-${p.fiscal_year}-q${i.no}`}>Pay</Button>
                      </div>
                    )}
                    {canEdit && i.status === "accrued" && (
                      <Button size="sm" className="btn-primary-blue h-7 text-xs" onClick={() => setPayTarget({ year: p.fiscal_year, inst: i })} data-testid={`cit-pay-${p.fiscal_year}-q${i.no}`}>Pay</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {p.finalized && (
            <div className="border-t border-slate-100 px-5 py-3 text-sm bg-slate-50/60" data-testid={`cit-finalized-${p.fiscal_year}`}>
              <b>Final return:</b> actual profit {fmt(p.finalized.actual_profit)} → final CIT {fmt(p.finalized.final_cit)} · installments paid {fmt(p.finalized.installments_paid)} ·{" "}
              <span className={p.finalized.balance_due > 0 ? "font-bold text-rose-600" : "font-bold text-emerald-700"}>
                {p.finalized.balance_due > 0 ? `balance due ${fmt(p.finalized.balance_due)}` : `refund/credit ${fmt(-p.finalized.balance_due)}`}
              </span> · due {formatDate(p.finalized.final_return_due)}
            </div>
          )}
        </div>
      ))}
      <TaxPayDialog open={!!payTarget} onClose={() => setPayTarget(null)} title={`Pay CIT Q${payTarget?.inst?.no} · FY${payTarget?.year}`} amount={payTarget?.inst?.amount} dueDate={payTarget?.inst?.due_date} onPay={pay} />
    </div>
  );
}
